import axios from 'axios'
import store from '@/store'
import helper from '@/helpers/helper'

// for multiple requests
let isRefreshing = false;
let failedQueue = [];

axios.defaults.baseURL = process.env.VUE_APP_API_BASE_URL + '/'

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  })

  failedQueue = [];
}

// Request interceptor for API calls
axios.interceptors.request.use(
  async config => {
    const token = store.state.auth.accessToken
    config.headers = {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded'
    }
    return config;
  },
  error => {
    return Promise.reject(error)
});

axios.interceptors.response.use(function (response) {
  return response;
}, async function (error) {

  const originalRequest = error.config || {};

  const isLogoutRequest = Boolean(originalRequest.url && originalRequest.url.includes('/logout'));

  // Bypass token refresh and logout for external services (e.g. integrated pentester platform) or explicitly skipped requests
  const isExternalUrl = originalRequest.url &&
    (originalRequest.url.startsWith('http://') || originalRequest.url.startsWith('https://')) &&
    !originalRequest.url.startsWith(process.env.VUE_APP_API_BASE_URL);

  if (originalRequest.skipAuthRefresh || isExternalUrl || isLogoutRequest) {
    return Promise.reject(error);
  }

  // When a 401 response occurs on a request whose URL contains /organisations/undefined or /organisations/null, do NOT trigger /users/token/refresh or auth/logout
  if (originalRequest.url && (originalRequest.url.includes('/organisations/undefined') || originalRequest.url.includes('/organisations/null'))) {
    return Promise.reject(error);
  }

  if (error.response && error.response.status === 401 && !originalRequest._retry && !originalRequest.skipAuthRefresh && !isLogoutRequest) {

    if (isRefreshing) {
      return new Promise(function(resolve, reject) {
        failedQueue.push({resolve, reject})
      }).then(token => {
        originalRequest.headers['Authorization'] = 'Bearer ' + token;
        return axios(originalRequest);
      }).catch(err => {
        return Promise.reject(err);
      })
    }

    originalRequest._retry = true;
    isRefreshing = true;

    let data = {
      id: store.state.auth.jwtPayload ? store.state.auth.jwtPayload.user_id : null,
      access_token: store.state.auth.accessToken,
      refresh_token: store.state.auth.jwtPayload ? store.state.auth.jwtPayload.jwt_refresh_token : null
    }

    return new Promise(function (resolve, reject) {
      axios.post('/users/token/refresh', data, { skipAuthRefresh: true })
        .then((response) => {
          const resBody = response?.data || {};
          const resData = resBody.data || resBody;
          const newAccessToken =
            resData?.user?.access_token ||
            resData?.access_token ||
            resBody?.access_token ||
            resBody?.user?.access_token;

          if (newAccessToken) {
            store.commit("auth/setAccessToken", newAccessToken);
            try {
              const decoded = helper.parseJwt(newAccessToken);
              if (decoded) {
                store.commit("auth/setJwtPayload", decoded);
              }
            } catch (decodeErr) {
              // Ignore JWT decode error on refresh
            }
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + newAccessToken;
            originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;
            processQueue(null, newAccessToken);
            resolve(axios(originalRequest));
          } else {
            const exp = store.state.auth.jwtPayload?.exp;
            const isStillValid = exp && (exp * 1000 > Date.now());
            processQueue(error, null);
            reject(error);
            if (!isStillValid) {
              store.commit('auth/setTokenExpires');
              store.dispatch('auth/logout', { logoutAuth: true });
            }
          }
        })
        .catch((err) => {
          const exp = store.state.auth.jwtPayload?.exp;
          const isStillValid = exp && (exp * 1000 > Date.now());
          processQueue(err, null);
          reject(err);
          if (!isStillValid) {
            store.commit('auth/setTokenExpires');
            store.dispatch('auth/logout', { logoutAuth: true });
          }
        })
        .finally(() => { isRefreshing = false })
    })
  }

  return Promise.reject(error);
});

export default {
  install: (app) => {
    app.config.globalProperties.$axios = axios
  }
}